import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationErrorStatus, AuthenticationRoleResponse, DomainType, Entity, TenantInfoRequest, TenantStatus } from '@jms/model';
import { ofAction } from '@jms/shared/ngrx-actions';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';;
import { toPayload } from '@jms/utils/util.redux';
import { UtilService } from '@jms/utils/util.services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { ApplicationActions, SetGeneralSetting } from './application.actions';
import { ApplicationActionName } from './application.state';
import { GeneralSettingService } from '@jms/ui/ui.standalone.services/general.setting.service';
import { ContextService } from '@jms/ui/ui.standalone.services/context.service';
import { SharedSocketService } from '@workbuddy/services/socket/shared.socket.service';
import { AuthTokenService } from '@jms/utils/util.authentication';

const environment = ConfigService.environment;

@Injectable()
export class ApplicationEffects {
    private authPath: string;
    private infoPath: string;
    private user: Entity;
    private returnUrl: string;

    constructor(
        // private appInsightsService: AppInsightsService,
        private http: HttpClient,
        private actions$: Actions,
        private context: ContextService,
        private router: Router,
        private applicationActions: ApplicationActions,
        private generalSettings: GeneralSettingService,
        private sharedSocket: SharedSocketService,
        private authTokenService: AuthTokenService
    ) {
        this.authPath = '/api/authentication/';
        this.infoPath = '/api/info';

        this.applicationActions.state$.subscribe(x => {
            this.user = x.user;
            this.returnUrl = x.returnUrl;
        });
    }


    logout$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.Logout),
            // Clear AI agent thread tabs so they don't leak to the next user on this browser
            tap(() => localStorage.removeItem('agent-tabs')),
            switchMap(() => from(this.authTokenService.logout())),
            map(() => {
                // Carry the tenant's advanced SSO hint to the id app so it can pin login to their Entra app.
                UtilService.navigate(environment.jms.logoutHref, `${this.context.origin}/callback`, UtilService.ssoHintParams(environment.sso));
            })
        ), { dispatch: false });


    authorise$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.LoginAuthorise),
            map(toPayload),
            switchMap(x =>
                this.authoriseLogin(x)
                    .pipe(
                        map(login => {

                            if (!login.success) {
                                switch (login.errorStatus) {
                                    case AuthenticationErrorStatus.UserNotActive:
                                        return this.applicationActions.routerNavigatating('/inactive/user');

                                    case AuthenticationErrorStatus.UserDeleted:
                                        return this.applicationActions.routerNavigatating('/deleted/user');

                                    case AuthenticationErrorStatus.UserInactiveStatus:
                                        return this.applicationActions.routerNavigatating('/inactive/status/user');

                                    case AuthenticationErrorStatus.UserCannotLoginViaWeb:
                                        return this.applicationActions.routerNavigatating('/login/access-denied');

                                    case AuthenticationErrorStatus.LoginsDisabled:
                                        return this.applicationActions.routerNavigatating('/login/maintenance');

                                    case AuthenticationErrorStatus.TokenWasUsed:
                                        return this.applicationActions.loginError(login, false);

                                    case AuthenticationErrorStatus.ConcurrentSessionLimitReached:
                                        return this.applicationActions.loginError(login, false);
                                }
                            }

                            return login.success && login.token
                                ? this.applicationActions.loginAuthorised(login.token, login.refreshToken, login.roleToken, x.returnUrl, false)
                                : this.applicationActions.logout(true, false);
                        }),
                        catchError(error => of(this.applicationActions.loginError({ message: error, success: false }, false)))
                    )
            )));

    private authoriseLogin(request: { domainType?: DomainType }): Observable<AuthenticationRoleResponse> {

        return this.http.post<AuthenticationRoleResponse>(this.authPath + 'role', request)
            .pipe(
                switchMap(login => {
                    if (login.success || request.domainType !== DomainType.Tenant || login.errorStatus !== AuthenticationErrorStatus.UserCannotLoginViaWeb) return of(login);

                    return this.http.post<AuthenticationRoleResponse>(this.authPath + 'role', { ...request, domainType: DomainType.ClientPortal })
                        .pipe(
                            switchMap(clientLogin => clientLogin.success || clientLogin.errorStatus !== AuthenticationErrorStatus.UserCannotLoginViaClient
                                ? of(clientLogin)
                                : this.http.post<AuthenticationRoleResponse>(this.authPath + 'role', { ...request, domainType: DomainType.ContractorPortal }))
                        );
                })
            );
    }


    authorised$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.LoginAuthorised),
            map(toPayload),
            map(x => {

                this.authTokenService.setToken(x.token, x.refreshToken);
                this.sharedSocket.reinitialize();
                const url = x.returnUrl ? x.returnUrl : '/home';
                // navigateByUrl, not navigate([url]): returnUrl is a full serialized path that can
                // include secondary outlets and query params (e.g. inspector after refresh).
                // navigate([url]) treats the string as route commands and drops (inspector:…).
                this.router.navigateByUrl(url, { replaceUrl: true });
            })
        ), { dispatch: false });


    routerNavigating$ = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.RouterNavigating),
            map(toPayload),
            map(x => this.router.navigate([x]))
        ), { dispatch: false });


    tenantInfo = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.GetTenantInfo),
            map(toPayload),
            switchMap(x =>
                this.http
                    .get<TenantInfoRequest>(this.infoPath)
                    .pipe(
                        map(info => {

                            const payload = {
                                info: info,
                                returnUrl: x
                            };

                            this.generalSettings.setGeneralSettings(info.generalSettings);

                            return this.applicationActions.actionSuccess(ApplicationActionName.GetTenantInfo, payload);
                        })
                    )
            )
        ));


    checkTenantStatus = createEffect(() => this.actions$
        .pipe(
            ofType(ApplicationActionName.ActionSuccess),
            map((x: any) => x.payload),
            filter(x => x.type === ApplicationActionName.GetTenantInfo),
            map(toPayload),
            map(x => x.info),
            withLatestFrom(this.applicationActions.state$),
            map(([info, state]) => {
                switch (info.tenantStatus) {
                    case TenantStatus.Created:
                        this.router.navigate(['/inactive/tenant']);
                        break;

                    case TenantStatus.Confirmed: {
                        this.router.navigate(['/setup']);
                        break;
                    }

                    case TenantStatus.Initializing: {
                        this.router.navigate(['home/initializing']);
                        break;
                    }

                    default: {
                        // AuthGuard now awaits tenant info and returns true so the *original*
                        // navigation completes — including secondary outlets (inspector on F5).
                        // Always calling navigateByUrl here used to re-enter after canActivate
                        // returned false; that second hop dropped named outlets.
                        //
                        // Only re-navigate when nothing is already activating that URL (safety net
                        // for edge cases). Skip when a navigation is in flight or we are already there.
                        const returnUrl = state.returnUrl;
                        if (!returnUrl) break;
                        if (this.router.getCurrentNavigation()) break;
                        if (this.router.url === returnUrl) break;
                        void this.router.navigateByUrl(returnUrl);
                        break;
                    }
                }
            })
        ), { dispatch: false });


    tracking$ = createEffect(() => this.actions$
        .pipe(
            // TODO: Review and break down into smaller track event with more detail. For example the sub action of success and failed actions.
            switchMap(x => {
                // this.appInsightsService.trackTrace(x.type);
                return of(null);
            })
        ), { dispatch: false });


    setGeneralSetting = createEffect(() => this.actions$
        .pipe(
            ofAction(SetGeneralSetting),
            map(action => {
                return this.applicationActions.actionSuccess(action.type, action.data);
            })
        ));
}
